import React, { useEffect, useRef, useState } from "react";

import { DeleteRegular, SpinnerIosRegular } from "@fluentui/react-icons";

import { StatusBanner } from "@/components/feature/StatusBanner";

import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { useDispatch, useSelector } from "react-redux";
import DateTime from "../components/Date";
import Host from "../components/Host";
import Location from "../components/Location";
import Room from "../components/Room";
import Visitors from "../components/Visitors";
import { fetchAndStoreOutlookId, useFetchOutlookIdOnLoad } from "../helpers/appointmentIdHelpers";
import { CHANGE_APPOINTMENT_REQUEST, CREATE_APPOINTMENT_REQUEST } from "../redux/actions";
import AppointmentDeleted from "./AppointmentDeleted";
import AppointmentFailed from "./AppointmentFailed";
import AppointmentSuccess from "./AppointmentSuccess";
import AppointmentUpdateSuccess from "./AppointmentUpdateSuccess";

const Portier = () => {
  const dispatch = useDispatch();
  const appointment = useSelector((state) => state.appointment);
  const emailStatus = useSelector((state) => state.mailstatus.emailStatus);
  const emailError = useSelector((state) => state.mailstatus.emailError);
  const deleteAppointmentStatus = useSelector((state) => state.deleteAppointment.deleteAppointmentStatus);
  const deleteAppointmentError = useSelector((state) => state.deleteAppointment.error);
  const appointmentStatus = useSelector((state) => state.createAppointment.appointmentStatus);
  const appointmentError = useSelector((state) => state.createAppointment.errors);
  const isGetAppointmentSuccessful = useSelector((state) => state.getAppointment.isSuccessful);
  const getAppointment = useSelector((state) => state.getAppointment);
  const changeVisitorStatus = useSelector((state) => state.changeVisitor.changevisitorStatus);
  const changeVisitorError = useSelector((state) => state.changeVisitor.errors);
  const addVisitorStatus = useSelector((state) => state.addVisitor.addvisitorStatus);
  const addVisitorError = useSelector((state) => state.addVisitor.errors);
  const changeAppointmentStatus = useSelector((state) => state.changeAppointment.changeappointmentStatus);
  const changeAppointmentError = useSelector((state) => state.changeAppointment.errors);
  const customerName = useSelector((state) => state.company.data.customer_name);
  const hasCheckedInVisitor =
    customerName === "ETH Zürich" && (getAppointment?.data?.visitors || []).some((v) => v.checked_in === true);

  const [showAppointmentSuccess, setShowAppointmentSuccess] = useState(false);
  // Was zuletzt gescheitert ist: `{ kind, detail }` — `kind` bestimmt den Text,
  // `detail` die technische Ursache für den Support.
  const [failure, setFailure] = useState(null);
  const [showAppointmentDeleted, setShowAppointmentDeleted] = useState(false);
  const [showAppointmentUpdateSuccess, setShowAppointmentUpdateSuccess] = useState(false);
  const [isVisitorsSaved, setIsVisitorsSaved] = useState(false);
  // Läuft gerade ein Versand? Steuert Beschriftung und Sperre der Schaltfläche.
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Fehlende Pflichtangaben aus der letzten Prüfung.
  const [missingFields, setMissingFields] = useState([]);
  const [checkedVisitorsCount, setCheckedVisitorsCount] = useState(0);

  const visitorsRef = useRef(null);

  useFetchOutlookIdOnLoad();

  useEffect(() => {
    if (appointment.outlook_id) {
      dispatch({
        type: "GET_APPOINTMENT_REQUEST",
        payload: { outlook_id: appointment.outlook_id },
      });
    }
  }, [appointment.outlook_id]);

  const validateVisitors = () => {
    if (visitorsRef.current && visitorsRef.current.triggerValidation) {
      const validationResults = visitorsRef.current.triggerValidation();
      if (!validationResults.isValid) {
        // `window.alert` ist im Task Pane nicht verfügbar — die fehlenden
        // Felder stehen deshalb im Banner über dem Formular.
        setMissingFields(validationResults.missingFields);
        return false;
      }
      setMissingFields([]);
      visitorsRef.current.createAppointmentVisitors();
    }
    return true;
  };

  const handleButtonClickSend = async () => {
    if (isGetAppointmentSuccessful) {
      setFailure(null);
      setIsSubmitting(true);
      dispatch(CHANGE_APPOINTMENT_REQUEST(appointment));
      return;
    }
    if (!validateVisitors()) return;
    setFailure(null);
    setIsSubmitting(true);
    // Ohne Outlook-ID gibt es nichts zu senden — der Versuch endete früher in
    // einer Schaltfläche, die dauerhaft "Wird gesendet…" anzeigte.
    const stored = await fetchAndStoreOutlookId(dispatch);
    if (stored.error) {
      setFailure({ kind: "outlook", detail: stored.error });
      setIsSubmitting(false);
      return;
    }
    setIsVisitorsSaved(true);
  };

  useEffect(() => {
    if (deleteAppointmentStatus === "success") {
      setShowAppointmentDeleted(true);
      setFailure(null);
      setShowAppointmentSuccess(false);
      setShowAppointmentUpdateSuccess(false);
    } else if (deleteAppointmentStatus === "failure") {
      setFailure({ kind: "delete", detail: deleteAppointmentError });
      setShowAppointmentDeleted(false);
      setShowAppointmentSuccess(false);
      setShowAppointmentUpdateSuccess(false);
    }
  }, [deleteAppointmentStatus, deleteAppointmentError]);

  useEffect(() => {
    if (appointmentStatus === "success" || appointmentStatus === "failure") {
      setIsSubmitting(false);
    }
    if (appointmentStatus === "success") {
      setShowAppointmentSuccess(true);
      setFailure(null);
      setShowAppointmentUpdateSuccess(false);
    } else if (appointmentStatus === "failure") {
      setFailure({ kind: "create", detail: appointmentError });
      setShowAppointmentSuccess(false);
      setShowAppointmentUpdateSuccess(false);
    }
  }, [appointmentStatus, appointmentError]);

  useEffect(() => {
    if (emailStatus === "success") {
      setShowAppointmentSuccess(true);
      setFailure(null);
      setShowAppointmentUpdateSuccess(false);
    } else if (emailStatus === "failure") {
      // Der Termin steht bereits — gescheitert ist nur der Versand.
      setFailure({ kind: "mail", detail: emailError });
      setShowAppointmentSuccess(false);
      setShowAppointmentUpdateSuccess(false);
    }
  }, [emailStatus, emailError]);

  useEffect(() => {
    if (changeVisitorStatus === "success" || addVisitorStatus === "success" || changeAppointmentStatus === "success") {
      setShowAppointmentUpdateSuccess(true);
      setShowAppointmentSuccess(false);
      setFailure(null);
      setShowAppointmentDeleted(false);
    } else if (
      changeVisitorStatus === "failure" ||
      addVisitorStatus === "failure" ||
      changeAppointmentStatus === "failure"
    ) {
      const detail =
        (changeVisitorStatus === "failure" && changeVisitorError) ||
        (addVisitorStatus === "failure" && addVisitorError) ||
        (changeAppointmentStatus === "failure" && changeAppointmentError) ||
        null;
      setFailure({ kind: "update", detail });
      setShowAppointmentUpdateSuccess(false);
      setShowAppointmentSuccess(false);
      setShowAppointmentDeleted(false);
    }
  }, [
    changeVisitorStatus,
    changeVisitorError,
    addVisitorStatus,
    addVisitorError,
    changeAppointmentStatus,
    changeAppointmentError,
  ]);

  const deepCompareFields = (fields1, fields2) => {
    const f1 = [...(fields1 || [])].sort((a, b) => (a.field_id || "").localeCompare(b.field_id || ""));
    const f2 = [...(fields2 || [])].sort((a, b) => (a.field_id || "").localeCompare(b.field_id || ""));
    if (f1.length !== f2.length) {
      return false;
    }

    for (let i = 0; i < f1.length; i++) {
      const field1 = f1[i];
      const field2 = f2[i];
      if (
        field1.type !== field2.type ||
        field1.title !== field2.title ||
        field1.field_id !== field2.field_id ||
        (field1.is_shown || []).join(",") !== (field2.is_shown || []).join(",") ||
        field1.field_type !== field2.field_type ||
        field1.inserted_value !== field2.inserted_value ||
        (field1.accepted_values || []).join(",") !== (field2.accepted_values || []).join(",")
      ) {
        return false;
      }
    }
    return true;
  };

  const handleButtonClickUpdate = async () => {
    setFailure(null);
    if (isGetAppointmentSuccessful) {
      console.log("Existiert Bereits!");
      const originalVisitors = getAppointment.data.visitors || [];
      const updatedVisitors = visitorsRef.current.getVisitorsData();

      // Änderungen erkennen — Tags und Türberechtigungen zählen mit, sonst
      // bliebe eine reine Berechtigungs-Änderung unbemerkt.
      // `door_permissions` ist im Backend ein JSON-Feld mit `{}` als Default —
      // es kommt also nicht immer als Array an.
      const asArray = (value) => (Array.isArray(value) ? value : []);
      const sameLabels = (a, b) => {
        const left = asArray(a);
        const right = asArray(b);
        return left.length === right.length && left.every((label) => right.includes(label));
      };
      const samePermissions = (a, b) => {
        const left = asArray(a);
        const right = asArray(b);
        return (
          left.length === right.length &&
          left.every((permission) =>
            right.some((other) => other.ExosID === permission.ExosID),
          )
        );
      };

      const changedVisitors = updatedVisitors.filter((visitor) => {
        const originalVisitor = originalVisitors.find((ov) => ov.email.toLowerCase() === visitor.email.toLowerCase());
        if (!originalVisitor) return false;
        return (
          originalVisitor.name !== visitor.name ||
          originalVisitor.company !== visitor.company ||
          !deepCompareFields(originalVisitor.additional_information, visitor.dynamicFields) ||
          !sameLabels(
            (originalVisitor.tags || []).map((tag) => tag.tag_label),
            visitor.tagLabels || [],
          ) ||
          !samePermissions(originalVisitor.door_permissions || [], visitor.doorPermissions || [])
        );
      });

      // New visitors
      const newVisitors = updatedVisitors.filter(
        (visitor) =>
          !originalVisitors.find((ov) => ov.email.toLowerCase() === visitor.email.toLowerCase()) && visitor.isChecked,
      );

      // Removed visitors
      const removedVisitors = originalVisitors.filter((originalVisitor) => {
        const match = updatedVisitors.find((uv) => uv.email.toLowerCase() === originalVisitor.email.toLowerCase());
        return !match || !match.isChecked;
      });

      let isValid = true;
      if (changedVisitors.length > 0) {
        console.log("Updating visitors data...", changedVisitors);
        isValid = validateVisitors() && isValid;
        if (isValid) {
          dispatch({
            type: "CHANGE_VISITOR_DATA_REQUEST",
            payload: {
              outlook_id: appointment.outlook_id,
              visitors: changedVisitors.map((visitor) => ({
                id: originalVisitors.find((ov) => ov.email.toLowerCase() === visitor.email.toLowerCase())?.visitor_id,
                name: visitor.name,
                email: visitor.email,
                company: visitor.company,
                additional_information: visitor.dynamicFields.map((field) => ({
                  type: field.type || field.info_type,
                  title: field.title,
                  field_id: field.field_id,
                  is_shown: field.is_shown,
                  field_type: field.field_type,
                  inserted_value: field.value,
                  accepted_values: field.accepted_values,
                })),
                tag_labels: visitor.tagLabels || [],
                door_permissions: visitor.doorPermissions || [],
                language: visitor.language || undefined,
              })),
            },
          });
        } else {
          console.log("Validation failed for changed visitors.");
        }
      }

      if (newVisitors.length > 0) {
        console.log("Adding new visitors...", newVisitors);
        isValid = validateVisitors() && isValid;
        if (isValid) {
          dispatch({
            type: "ADD_VISITOR_TO_APPOINTMENT_REQUEST",
            payload: {
              outlook_id: appointment.outlook_id,
              visitors: newVisitors.map((visitor) => ({
                name: visitor.name,
                email: visitor.email,
                company: visitor.company,
                additional_information: visitor.dynamicFields.map((field) => ({
                  type: field.type || field.info_type, // Ensure type is included
                  title: field.title,
                  field_id: field.field_id,
                  is_shown: field.is_shown,
                  field_type: field.field_type,
                  inserted_value: field.value,
                  accepted_values: field.accepted_values,
                })),
                tag_labels: visitor.tagLabels || [],
                door_permissions: visitor.doorPermissions || [],
                language: visitor.language || undefined,
              })),
            },
          });
        } else {
          console.log("Validation failed for new visitors.");
        }
      }

      if (removedVisitors.length > 0) {
        console.log("Removing visitors...", removedVisitors);
        removedVisitors.forEach((visitor) => {
          dispatch({
            type: "DELETE_VISITOR_FROM_APPOINTMENT_REQUEST",
            payload: {
              outlook_id: appointment.outlook_id,
              visitor_id: visitor.visitor_id,
            },
          });
        });
      }

      if (isValid) {
        dispatch(CHANGE_APPOINTMENT_REQUEST(appointment));
      }
    } else {
      console.log("existiert nicht!");
    }
  };

  const deleteButtonClick = async () => {
    if (appointment.outlook_id) {
      dispatch({ type: "DELETE_APPOINTMENT_REQUEST", payload: { outlook_id: appointment.outlook_id } });
      console.log("Appointment gelöscht!");
    } else {
      console.log("Keine outlook_id verfügbar zum Löschen");
    }
  };

  useEffect(() => {
    if (isVisitorsSaved && appointment.outlook_id && validateVisitors()) {
      dispatch(CREATE_APPOINTMENT_REQUEST(appointment));
      setIsVisitorsSaved(false);
    }
  }, [isVisitorsSaved, appointment.outlook_id, dispatch]);

  // Nach dem Versand nach oben rollen — die Bestätigung steht dort, während der
  // Blick zuletzt unten bei der Schaltfläche war.
  useEffect(() => {
    if (showAppointmentSuccess || showAppointmentUpdateSuccess || showAppointmentDeleted) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [showAppointmentSuccess, showAppointmentUpdateSuccess, showAppointmentDeleted]);

  useEffect(() => {
    if (
      changeVisitorStatus === "failure" ||
      addVisitorStatus === "failure" ||
      changeAppointmentStatus === "failure"
    ) {
      setIsSubmitting(false);
    }
  }, [changeVisitorStatus, addVisitorStatus, changeAppointmentStatus]);

  const formattedDate = getAppointment?.data?.created_at
    ? new Date(getAppointment.data.created_at).toLocaleDateString("de-DE")
    : null;
  const formattedTime = getAppointment?.data?.created_at
    ? new Date(getAppointment.data.created_at).toLocaleTimeString("de-DE").slice(0, -3)
    : null;

  return (
    <div className="px-[1.6rem] pb-[1.6rem]">
      {/* Gelöschte Termine haben keinen Inhalt mehr — dort bleibt die
          Vollbild-Meldung. Erfolg und Fehler erscheinen als Banner über dem
          Formular, damit der Kontext erhalten bleibt. */}
      {showAppointmentDeleted ? (
        <AppointmentDeleted />
      ) : (
        <>
          {isGetAppointmentSuccessful && formattedDate && (
            <StatusBanner tone="success" title="Einladung versendet" className="mt-[1.2rem]">
              {formattedDate}, {formattedTime} Uhr
            </StatusBanner>
          )}
          {missingFields.length > 0 && (
            <StatusBanner
              tone="danger"
              title="Pflichtangaben fehlen"
              className="mt-[1.2rem]"
            >
              {[...new Set(missingFields)].join(", ")}
            </StatusBanner>
          )}
          {showAppointmentSuccess && <AppointmentSuccess />}
          {failure && <AppointmentFailed kind={failure.kind} detail={failure.detail} />}
          {showAppointmentUpdateSuccess && <AppointmentUpdateSuccess />}
          <div
            className={
              showAppointmentSuccess || showAppointmentUpdateSuccess
                ? "opacity-60 pointer-events-none"
                : undefined
            }
          >
          {/* Termin-Angaben in einem Block: feste Werte (Datum, Raum) und
              Auswahlfelder (Standort, Gastgeber) gleich aufgebaut. */}
          <div className="mt-[1.2rem] rounded-card border border-gray bg-white px-[1.4rem] py-[1.4rem] space-y-[1.2rem]">
            <DateTime />
            <Room />
            <Location />
            <Host />
          </div>
          <Visitors ref={visitorsRef} onCheckedVisitorsChange={setCheckedVisitorsCount} />
          </div>
          {/* Nach erfolgreichem Versand bzw. Update gibt es hier nichts mehr zu
              tun — der Termin wird über Outlook selbst abgeschlossen. */}
          {!showAppointmentSuccess && !showAppointmentUpdateSuccess && (
            <>
              <div className="h-[7rem]" />
              <div className="w-full bg-white border-t border-gray p-[1.2rem] fixed inset-x-0 bottom-0 flex items-center gap-[0.8rem]">
            {!isGetAppointmentSuccessful ? (
              <Button
                type="button"
                onClick={handleButtonClickSend}
                disabled={checkedVisitorsCount === 0 || isSubmitting}
                className="flex-1"
              >
                {isSubmitting ? (
                  <>
                    <SpinnerIosRegular className="w-[1.6rem] h-[1.6rem] animate-spin" />
                    Wird gesendet…
                  </>
                ) : (
                  "Einladung senden"
                )}
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleButtonClickUpdate}
                disabled={isSubmitting}
                className="flex-1"
              >
                {isSubmitting ? (
                  <>
                    <SpinnerIosRegular className="w-[1.6rem] h-[1.6rem] animate-spin" />
                    Wird gespeichert…
                  </>
                ) : (
                  "Aktualisieren"
                )}
              </Button>
            )}
                {isGetAppointmentSuccessful && !hasCheckedInVisitor && (
                  <IconButton
                    variant="danger"
                    size="md"
                    label="Termin löschen"
                    onClick={deleteButtonClick}
                    className="rounded-[0.8rem] shrink-0"
                  >
                    <DeleteRegular />
                  </IconButton>
                )}
              </div>
              {checkedVisitorsCount === 0 && !isGetAppointmentSuccessful && (
                <p className="fixed inset-x-0 bottom-[6.4rem] text-center text-[1.15rem] text-dim m-0">
                  Mindestens einen Teilnehmer auswählen
                </p>
              )}
            </>
          )}
          </>
        )}
    </div>
  );
};

export default Portier;
